// src/components/MyPayPanel.js
// ---------------------------------------------------------------------------
// "My Pay" on Home for a signed-in SUB CONTRACTOR (workerType Sub Contractor).
// Shows only their own money:
//   earned on each job (from the same owed lines the back end uses),
//   every payment - when, how much, how (check / ACH / cash...), check #,
//   and what is still owed to them.
// Read-only. Collapsed by default; reads Firestore only while open.
//
//   <MyPayPanel currentUser={currentUserData} />
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { isSubcontractor } from "../utils/contractorIdentity";
import { describeTerm, lineOwed, round2 } from "../utils/subPayTerms";
import { loadAllSubPayments, loadPeople } from "../utils/subPayPayments";
import { fmtMoney } from "./SubPayTermFields";
import "./SubPayPanel.css";
import { useLanguage, tNow } from "../i18n";
import { describeTermFor } from "./describeTermI18n";

const dayKey = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};

const MyPayPanel = ({ currentUser }) => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [balanceOnly, setBalanceOnly] = useState(false);

  const isSub = !!currentUser?.id && isSubcontractor(currentUser);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [people, jobsSnap, occSnap, payments] = await Promise.all([
        loadPeople(true),
        getDocs(collection(db, "jobs")),
        getDocs(collection(db, "recurring_job_occurrences")).catch(() => ({ docs: [] })),
        loadAllSubPayments(),
      ]);
      setData({
        people,
        jobs: jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        occurrences: occSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        payments,
      });
    } catch (err) {
      console.error("My Pay: could not load", err);
      setError(tNow("Your pay could not load. Check your connection and try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && isSub) load();
  }, [open, isSub, load]);

  const view = useMemo(() => {
    if (!data || !currentUser) return null;
    const { people, jobs, occurrences, payments } = data;
    const identity = people.identities.find((i) => i.ids.has(currentUser.id));
    const myKey =
      identity?.key ||
      people.bucketKeyForName(`${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim());
    const mine = (name) => Boolean(name) && people.bucketKeyForName(name) === myKey;

    const jobsById = {};
    jobs.forEach((j) => {
      jobsById[j.id] = j;
    });

    const rows = [];
    jobs.forEach((job) => {
      (Array.isArray(job.subcontractorPay) ? job.subcontractorPay : []).forEach((line) => {
        if (!mine(line?.subcontractorName || line?.name)) return;
        const term = Object.values(job.subPayTerms || {}).find((t) => t?.name && mine(t.name));
        rows.push({
          key: `job:${job.id}`,
          jobId: job.id,
          occurrenceKey: "",
          wo: job.wo || "",
          site: job.siteName || "",
          date: dayKey(job.startDate),
          endDate: dayKey(job.endDate),
          status: job.status || "",
          how: term ? describeTerm(job, term, fmtMoney) : "",
          howJob: term ? job : null,
          howTerm: term || null,
          owed: lineOwed(line),
        });
      });
    });
    occurrences.forEach((occ) => {
      (Array.isArray(occ.subcontractorPay) ? occ.subcontractorPay : []).forEach((line) => {
        if (!mine(line?.subcontractorName || line?.name)) return;
        const job = jobsById[occ.originalJobId] || {};
        rows.push({
          key: `occ:${occ.id}`,
          jobId: occ.originalJobId || "",
          occurrenceKey: occ.id,
          wo: occ.wo || job.wo || "",
          site: job.siteName || "",
          date: dayKey(occ.occurrenceDate || occ.startDate),
          endDate: "",
          status: occ.status || job.status || "",
          how: "Visit",
          howIsVisit: true,
          owed: lineOwed(line),
        });
      });
    });

    const myPays = payments.filter(
      (p) => mine(p.subcontractorName) || (p.userId && p.userId === currentUser.id) || p.subcontractorId === currentUser.id
    );

    // Merge duplicate rows for the same job (two lines under two spellings)
    const byKey = new Map();
    rows.forEach((r) => {
      const prev = byKey.get(r.key);
      if (prev) prev.owed = round2(prev.owed + r.owed);
      else byKey.set(r.key, { ...r });
    });
    const list = Array.from(byKey.values()).map((r) => {
      const pays = myPays.filter((p) => (r.occurrenceKey ? p.occurrenceKey === r.occurrenceKey : p.jobId === r.jobId && !p.occurrenceKey));
      const paid = round2(pays.reduce((s, p) => s + p.amount, 0));
      return { ...r, paid, balance: round2(r.owed - paid) };
    });
    list.sort((a, b) => String(b.date).localeCompare(String(a.date)));

    const listed = new Set(list.map((r) => (r.occurrenceKey ? `o:${r.occurrenceKey}` : `j:${r.jobId}`)));
    const unallocated = myPays.filter((p) => !listed.has(p.occurrenceKey ? `o:${p.occurrenceKey}` : `j:${p.jobId}`));

    const earned = round2(list.reduce((s, r) => s + r.owed, 0));
    const paid = round2(myPays.reduce((s, p) => s + p.amount, 0));
    const jobLabel = (p) => {
      if (p.jobLabel) return p.jobLabel;
      const j = jobsById[p.jobId];
      return j ? `WO ${j.wo || "—"} / ${j.siteName || ""}` : tNow("General (not tied to a job)");
    };
    return {
      name: identity?.displayName || `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim(),
      rows: list,
      payments: myPays.slice().sort((a, b) => String(b.date).localeCompare(String(a.date))),
      unallocatedTotal: round2(unallocated.reduce((s, p) => s + p.amount, 0)),
      earned,
      paid,
      balance: round2(earned - paid),
      jobLabel,
    };
  }, [data, currentUser]);

  if (!isSub) return null;

  const rows = view ? (balanceOnly ? view.rows.filter((r) => Math.abs(r.balance) > 0.004) : view.rows) : [];

  return (
    <div className="spp-panel spp-mypay">
      <button type="button" className="spp-panel__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{open ? "▾" : "▸"} {t("My Pay")}</span>
        {view && (
          <span className="spp-panel__sum">
            {t("Earned")} {fmtMoney(view.earned)} · {t("Paid")} {fmtMoney(view.paid)} · {t("Owed to you")} <b>{fmtMoney(view.balance)}</b>
          </span>
        )}
      </button>

      {open && (
        <div className="spp-panel__body">
          <div className="spp-panel__bar">
            <button type="button" className="spp-link" onClick={load} disabled={loading}>
              ↻ {t("Refresh")}
            </button>
            {loading && <span className="spp-muted">{t("Loading…")}</span>}
          </div>
          {error && <div className="spp-warn">{error}</div>}

          {view && (
            <>
              <div className="spp-stats spp-stats--big">
                <div>
                  <span>{t("Earned")}</span>
                  <b>{fmtMoney(view.earned)}</b>
                </div>
                <div>
                  <span>{t("Paid to you")}</span>
                  <b>{fmtMoney(view.paid)}</b>
                </div>
                <div>
                  <span>{t("Still owed to you")}</span>
                  <b className={view.balance > 0.004 ? "spp-owing" : "spp-clear"}>{fmtMoney(view.balance)}</b>
                </div>
              </div>
              {view.unallocatedTotal > 0 && (
                <div className="spp-muted">
                  {t("{amount} of your payments weren't tied to one job. They count toward your total.", {
                    amount: fmtMoney(view.unallocatedTotal),
                  })}
                </div>
              )}

              <div className="spp-section-head">
                <h4>{t("My jobs")}</h4>
                <label className="spp-check">
                  <input type="checkbox" checked={balanceOnly} onChange={(e) => setBalanceOnly(e.target.checked)} /> {t("Only jobs with a balance")}
                </label>
              </div>
              {rows.length ? (
                <div className="spp-scroll">
                  <table className="spp-table">
                    <thead>
                      <tr>
                        <th>{t("Date")}</th>
                        <th>{t("WO / Site")}</th>
                        <th>{t("Pay")}</th>
                        <th>{t("Earned")}</th>
                        <th>{t("Paid")}</th>
                        <th>{t("Balance")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.key}>
                          <td>
                            {r.date}
                            {r.endDate && r.endDate !== r.date ? ` – ${r.endDate}` : ""}
                          </td>
                          <td>
                            {r.wo ? `WO ${r.wo}` : "—"} · {r.site}
                            {r.status ? <div className="spp-muted">{t(r.status)}</div> : null}
                          </td>
                          <td>{r.howIsVisit ? t("Visit") : r.howTerm ? describeTermFor(lang, r.howJob, r.howTerm, fmtMoney) : r.how || "—"}</td>
                          <td>{fmtMoney(r.owed)}</td>
                          <td>{fmtMoney(r.paid)}</td>
                          <td className={r.balance > 0.004 ? "spp-owing" : "spp-clear"}>{fmtMoney(r.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="spp-muted">{t("No jobs with pay yet.")}</div>
              )}

              <h4>{t("Payments")}</h4>
              {view.payments.length ? (
                <div className="spp-scroll">
                  <table className="spp-table">
                    <thead>
                      <tr>
                        <th>{t("Date")}</th>
                        <th>{t("Amount")}</th>
                        <th>{t("How")}</th>
                        <th>{t("Check # / Ref")}</th>
                        <th>{t("For")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {view.payments.map((p) => (
                        <tr key={`${p.sourceCollection}:${p.id}`}>
                          <td>{p.date}</td>
                          <td>{fmtMoney(p.amount)}</td>
                          <td>{p.method ? t(p.method) : "—"}</td>
                          <td>{p.reference || "—"}</td>
                          <td>{view.jobLabel(p)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="spp-muted">{t("No payments yet.")}</div>
              )}
              <div className="spp-muted">
                {t("Questions about a payment? Contact the office. Amounts update when the office records a payment.")}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MyPayPanel;
